import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge/Badge';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconAssociate from '@/icons/Associate.svg';
import IconCloth from '@/icons/Cloth.svg';
import IconSparePart from '@/icons/SparePart.svg';
import IconTools from '@/icons/Tools.svg';
import IconUsers from '@/icons/Users.svg';
import IconWorkshop from '@/icons/Workshop.svg';
import { AGREEMENT_TYPE_INFO, AgreementFromGetMany } from '@/models/agreements';
import { formatListWithMore } from '@/utils/list';

export const AGREEMENTS_PER_PAGE = 20;

const MAX_NAMES_SHOWN = 4;

type AgreementTableItem = AgreementFromGetMany;

interface AgreementsTableProps {
  agreements: AgreementTableItem[];
  page: number;
  onPageChange: (page: number) => void;
  count: number | undefined;
  emptyText?: string;
}

export const AgreementsTable = ({
  agreements,
  page,
  onPageChange,
  count,
  emptyText,
}: AgreementsTableProps) => {
  const { t } = useLingui();

  const defaultEmptyText = t({
    id: 'settings.organization.entities.agreements.table.empty',
    message: 'No agreements yet.',
  });

  return (
    <Table
      ariaLabel={t({
        id: 'settings.organization.entities.agreements.table.label',
        message: 'Agreements',
      })}
      columnWidths={['auto', '1.5fr', '1fr', '1fr']}
      bordered
    >
      <Header>
        <Row>
          <Column>
            <Trans id="settings.organization.entities.agreements.table.header.type">Type</Trans>
          </Column>
          <Column>
            <Trans id="settings.organization.entities.agreements.table.header.involved-entities">
              Involved entities
            </Trans>
          </Column>
          <Column>
            <Trans id="settings.organization.entities.agreements.table.header.applies-when">
              Applies when
            </Trans>
          </Column>
          <Column>
            <Trans id="settings.organization.entities.agreements.table.header.additional-info">
              Additional information
            </Trans>
          </Column>
        </Row>
      </Header>
      <Body>
        {agreements.length === 0 ? (
          <Row>
            <Cell isWholeRow>
              <Paragraph color="secondary">{emptyText ?? defaultEmptyText}</Paragraph>
            </Cell>
          </Row>
        ) : (
          agreements.map((agreement) => <AgreementRow key={agreement.id} agreement={agreement} />)
        )}
      </Body>
      <PaginationFooter
        itemsPerPage={AGREEMENTS_PER_PAGE}
        page={page}
        onPageChange={onPageChange}
        count={count}
      />
    </Table>
  );
};

const AgreementRow = ({ agreement }: { agreement: AgreementTableItem }) => {
  const { i18n } = useLingui();

  const entitiesAndGroupsA = [...agreement.entitiesA, ...agreement.entityGroupsA];
  const entitiesAndGroupsB = [...agreement.entitiesB, ...agreement.entityGroupsB];

  const typeInfo = AGREEMENT_TYPE_INFO[agreement.type];

  return (
    <Row to={`/settings_beta/network/agreements/${agreement.id}`}>
      <Cell>
        <Badge size="x-small" color={typeInfo.badgeColor}>
          {i18n._(typeInfo.label)}
        </Badge>
      </Cell>
      <Cell isLink className="sentry-mask">
        <Stack>
          <EntitiesGroupsOrClients
            entitiesAndGroups={entitiesAndGroupsA}
            hasClient={agreement.clientA}
          />
          <EntitiesGroupsOrClients
            entitiesAndGroups={entitiesAndGroupsB}
            hasClient={agreement.clientB}
          />
        </Stack>
      </Cell>
      <Cell>
        <AppliesWhen agreement={agreement} />
      </Cell>
      <Cell>
        {agreement.type === 'after_sales' && <AfterSalesAdditionalInfo agreement={agreement} />}
        {agreement.type === 'price_catalog' && <PriceCatalogAdditionalInfo agreement={agreement} />}
        {agreement.type === 'transit' && '-'}
      </Cell>
    </Row>
  );
};

const EntitiesGroupsOrClients = ({
  entitiesAndGroups,
  hasClient,
}: {
  entitiesAndGroups: { id: string; name: string }[];
  hasClient: boolean;
}) => {
  if (entitiesAndGroups.length === 0 && !hasClient) {
    return '-';
  }

  return (
    <Stack row gap="0.5rem" alignItems="center" flexWrap="nowrap">
      {entitiesAndGroups.length > 0 && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconAssociate style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            {formatListWithMore(
              entitiesAndGroups.map((e) => e.name),
              MAX_NAMES_SHOWN
            )}
          </Paragraph>
        </Stack>
      )}
      {hasClient && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconUsers style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            <Trans id="settings.organization.entities.agreements.table.clients">Clients</Trans>
          </Paragraph>
        </Stack>
      )}
    </Stack>
  );
};

const AppliesWhen = ({ agreement }: { agreement: AgreementTableItem }) => {
  if (agreement.articleWorkshops.length === 0 && agreement.articleBrands.length === 0) {
    return '-';
  }

  return (
    <Stack gap="0.25rem">
      {agreement.articleWorkshops.length > 0 && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconWorkshop style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            {formatListWithMore(
              agreement.articleWorkshops.map(({ name }) => name),
              MAX_NAMES_SHOWN
            )}
          </Paragraph>
        </Stack>
      )}
      {agreement.articleBrands.length > 0 && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconCloth style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            {formatListWithMore(
              agreement.articleBrands.map(({ name }) => name),
              MAX_NAMES_SHOWN
            )}
          </Paragraph>
        </Stack>
      )}
    </Stack>
  );
};

const AfterSalesAdditionalInfo = ({ agreement }: { agreement: AgreementTableItem }) => {
  if (!agreement.clientB || agreement.supportedCountries.length === 0) {
    return '-';
  }

  const countries = formatListWithMore(agreement.supportedCountries, MAX_NAMES_SHOWN);

  return (
    <Paragraph>
      <Trans id="settings.organization.entities.agreements.table.additional-info.supported-countries">
        Client countries: {countries}
      </Trans>
    </Paragraph>
  );
};

const PriceCatalogAdditionalInfo = ({ agreement }: { agreement: AgreementTableItem }) => {
  const actions = agreement.prices.filter((price) => price.actionTypeOrganizationId);
  const packs = agreement.prices.filter((price) => price.packActionTypeOrganizationId);
  const spareParts = agreement.prices.filter((price) => price.sparePartTypeOrganizationId);
  const fees = agreement.prices.filter((price) => price.feeTypeId);

  return (
    <Stack>
      {packs.length + actions.length > 0 && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconTools style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            {packs.length > 0 && actions.length > 0 ? (
              <Trans id="settings.organization.entities.agreements.table.additional-info.packs-and-actions">
                {packs.length} packs and {actions.length} actions
              </Trans>
            ) : packs.length > 0 ? (
              <Trans id="settings.organization.entities.agreements.table.additional-info.packs">
                {packs.length} packs
              </Trans>
            ) : (
              <Trans id="settings.organization.entities.agreements.table.additional-info.actions">
                {actions.length} actions
              </Trans>
            )}
          </Paragraph>
        </Stack>
      )}
      {spareParts.length > 0 && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconSparePart style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            <Trans id="settings.organization.entities.agreements.table.additional-info.spare-parts">
              {spareParts.length} spare parts
            </Trans>
          </Paragraph>
        </Stack>
      )}
      {fees.length > 0 && (
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconAdd style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            <Trans id="settings.organization.entities.agreements.table.additional-info.fees">
              {fees.length} fees
            </Trans>
          </Paragraph>
        </Stack>
      )}
    </Stack>
  );
};
